import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Formik, Form } from 'formik';
import Button from 'apollo-react/components/Button';
import Grid from 'apollo-react/components/Grid';
import Box from 'apollo-react/components/Box';
import Typography from '../../StyledTypography';
import TextInput from '../../Form/textInput';
import CommonModal from '../../CommonModal/CommonModal';
import { FormikDocumentUpload } from '../../FormikComponent';
import Trash from 'apollo-react-icons/Trash';
import Paperclip from 'apollo-react-icons/Paperclip';
import IconButton from 'apollo-react/components/IconButton';
import { toast } from 'react-toastify';
import styles from './EditCommentModal.module.css';

const EditCommentModal = ({
  open,
  onClose,
  comment,
  onUpdateComment,
  requestId,
  isAdditionalDocument = false,
}) => {
  const { t } = useTranslation();
  const [removedFileIds, setRemovedFileIds] = useState([]);

  const existingAttachments = comment?.attachments || [];
  const visibleAttachments = existingAttachments.filter(
    (att) => !removedFileIds.includes(att.file?.id)
  );

  const initialValues = {
    comment: comment?.comment || '',
    newFiles: [],
  };

  const handleRemoveFile = (fileId) => {
    setRemovedFileIds((prev) => [...prev, fileId]);
  };

  const handleUndoRemoveFile = (fileId) => {
    setRemovedFileIds((prev) => prev.filter((id) => id !== fileId));
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    if (!values.comment || !values.comment.trim()) {
      toast.error(t('enter_comment'), { position: 'top-center' });
      setSubmitting(false);
      return;
    }

    try {
      const newFiles =
        values.newFiles && values.newFiles.length > 0 ? values.newFiles : null;

      await onUpdateComment({
        id: comment.id,
        comment: values.comment,
        attachedFilesToAdd: newFiles,
        attachedFilesToRemove:
          removedFileIds.length > 0 ? removedFileIds : null,
        requestId,
        isAdditionalDocument,
      });

      toast.success(t('comment_updated'), { position: 'top-center' });
      setRemovedFileIds([]);
      onClose();
    } catch (error) {
      console.error('Error updating comment:', error);
      toast.error(t('something_went_wrong'), { position: 'top-center' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setRemovedFileIds([]);
    onClose();
  };

  return (
    <CommonModal
      open={open}
      onClose={handleClose}
      title={t('edit_comment')}
      hideButtons
      size="medium"
      fullWidth
    >
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue, isSubmitting }) => (
          <Form>
            <Grid container spacing={2} className={styles.formContainer}>
              <Grid size={12}>
                <TextInput
                  label={t('comment')}
                  placeholder={t('enter_comment_placeholder')}
                  multiline
                  minRows={2}
                  textareaHeight="auto"
                  showInfoIcon={false}
                  fullWidth
                  value={values.comment}
                  onChange={(val) => setFieldValue('comment', val)}
                />
              </Grid>

              {existingAttachments.length > 0 && (
                <Grid size={12}>
                  <Typography
                    variant="bodySmall"
                    className={styles.sectionLabel}
                  >
                    {t('attachments')}
                  </Typography>
                  {existingAttachments.map(({ file }) => {
                    if (!file) return null;
                    const isRemoved = removedFileIds.includes(file.id);
                    return (
                      <Box
                        key={file.id}
                        className={`${styles.attachmentRow} ${isRemoved ? styles.attachmentRemoved : ''}`}
                      >
                        <Box className={styles.attachmentInfo}>
                          <Paperclip
                            size="small"
                            className={styles.attachmentIcon}
                          />
                          <Typography
                            variant="bodySmall"
                            className={styles.attachmentName}
                          >
                            {file.original_name || `attachment_${file.id}`}
                          </Typography>
                        </Box>
                        {isRemoved ? (
                          <Button
                            size="small"
                            color="secondary"
                            onClick={() => handleUndoRemoveFile(file.id)}
                          >
                            {t('undo')}
                          </Button>
                        ) : (
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveFile(file.id)}
                            className={styles.removeButton}
                          >
                            <Trash size="small" />
                          </IconButton>
                        )}
                      </Box>
                    );
                  })}
                </Grid>
              )}

              <Grid size={12}>
                <FormikDocumentUpload
                  name="newFiles"
                  label={t('add_attachment')}
                  helperText={t('upload_file_supported_formats')}
                  accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,.csv,.jpg,.jpeg,.png,.gif,.zip,.rar,.7z"
                  required={false}
                  maxFiles={1}
                  multiple={false}
                />
              </Grid>
            </Grid>

            <Box className={styles.buttonContainer}>
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleClose}
              >
                {t('cancel')}
              </Button>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                disabled={isSubmitting}
              >
                {t('update_comment')}
              </Button>
            </Box>
          </Form>
        )}
      </Formik>
    </CommonModal>
  );
};

export default EditCommentModal;
